import { useUserStore } from '@/store/userStore';
import {
  shouldHardReload,
  handleHardReload,
  assigneeUserHasAccessToRoute,
  salesUserHasAccessToRoute,
  originatorUserHasAccessToRoute,
  saveDesiredRouteAfterLogin, handleHardReloadIframe,
} from './utils';
import { checkAssignee, checkOriginator } from '@/service/Leads';

const PUBLIC_PAGES = ['sign_in', 'forgot_password', 'reset_password', 'error', 'health-check', 'maintenance'];

export async function authGuard(to, from, next) {
  if (to.name === 'maintenance') {
    return next();
  }

  const userStore = useUserStore();

  if (!userStore.user || PUBLIC_PAGES.includes(to.name) || Object.keys(userStore.user).length === 0) {
    try {
      await userStore.fetchUser();

      if (userStore.userId) {
        userStore.setUserHomePath();
      }
    } catch (error) {
      return next({ name: 'error' });
    }
  }

  let routeAfterLogin = sessionStorage.getItem('desiredRouteAfterLogin');

  if (to.name === 'home') {
    if (userStore.isAdmin && !routeAfterLogin) {
      return next();
    }
    if (userStore.userId && !routeAfterLogin) {
      return next(userStore.homePath);
    }
    if (!userStore.isAdmin && !userStore.userId) {
      return next({ name: 'sign_in' });
    }
  }

  if (PUBLIC_PAGES.includes(to.name) && !userStore.userId) {
    userStore.$reset?.();
    return next();
  }

  if (to.meta.roles && Array.isArray(to.meta.roles) && to.meta.roles.length > 0) {

    if (userStore.isAdmin) {
      if (routeAfterLogin) {
        to = JSON.parse(routeAfterLogin);
        sessionStorage.removeItem('desiredRouteAfterLogin');
        return next(to.fullPath);
      }
      return next();
    }

    if (!userStore.userId && to.fullPath !== '/unauthorized') {
      saveDesiredRouteAfterLogin(to.fullPath, to.name, to.meta, to.params);
    }

    if (from.fullPath.includes('/sign_in') || to.fullPath.includes('after_sso=true')) {
      if (userStore.userId && routeAfterLogin) {
        to = JSON.parse(routeAfterLogin);
        sessionStorage.removeItem('desiredRouteAfterLogin');
      }
    }

    const userRoleNames = userStore.roles.map(role => role.name);
    const allowedRoles = to.meta.roles;

    const hasAccess = allowedRoles.some(role => userRoleNames.includes(role));

    if (hasAccess && routeAfterLogin) {
      return next(to.fullPath);
    }

    const isALeadActionRoute = to.fullPath.includes('leads');

    if (!hasAccess && isALeadActionRoute) {
      const leadId = to.params.id;
      const routeName = to.name;

      if (leadId) {
        if (assigneeUserHasAccessToRoute(routeName) && await checkAssignee(leadId)) {
          return routeAfterLogin ? next(to.fullPath) : next();
        }

        if (userStore.hasSalesJobFunction && salesUserHasAccessToRoute(routeName) && await checkAssignee(leadId)) {
          return routeAfterLogin ? next(to.fullPath) : next();
        }

        if (originatorUserHasAccessToRoute(routeName) && await checkOriginator(leadId)) {
          return routeAfterLogin ? next(to.fullPath) : next();
        }

        if (!userStore.userId) {
          return next({ name: 'sign_in' });
        }
        return next({ name: 'unauthorized' });
      }
    }

    const blankUserRoleAllowedRoutes = ['no-dashboard-access', 'user-details', 'edit-user']

    if (userStore.roles.length === 0 && blankUserRoleAllowedRoutes.includes(to.name)) {
      return next();
    }

    if (!hasAccess) {
      if (!userStore.userId) {
        return next({ name: 'sign_in' });
      }
      return next({ name: 'unauthorized' });
    }
  }

  if (PUBLIC_PAGES.includes(to.name) && userStore.userId) {
    if (to.name === 'sign_in' && typeof to.query.return_to === 'string') {
      return next();
    }

    return next(userStore.homePath);
  }

  next();
}

export async function errorHandler(error, to) {
  if (!shouldHardReload(error)) return;

  const isInIframe = window.self !== window.top;
  if (isInIframe) {
    await handleHardReloadIframe(to?.fullPath || window.location.href);
  } else {
    await handleHardReload(to?.fullPath || window.location.href);
  }
}
